import { UserActionDto } from '@treasury/api/channel';
import { Nullable } from '@treasury/utils/types';
import { AccountDto, isNullAccount } from './accounts.dto';

export const itemStatuses = Object.freeze([
    'Issued',
    'Cleared',
    'Returned',
    'Void',
    'Deleted',
    'StopPay',
    'StaleDate',
    'Dormant',
    'Failed',
    'Submitted',
] as const);

export type IssuedItemStatus = (typeof itemStatuses)[number];

export interface AuditItemDto {
    createdFiDateTime: string;
    createdByUserName: string;
    action: string;
    oldValue?: string | null;
    newValue?: string | null;
}

export type IssuedItemEntryType = 'Manual' | 'Upload' | 'Transmitted' | 'Other';
export type IssuedItemType = 'Check' | 'Void';

/**
 * @deprecated
 *
 * This is the legacy shape coming from `/arp` endpoints
 * and should be phased out in favor of `IssuedItemDto` and the end `/issuedItems`
 * endpoints.
 *
 * 11/13/2025 - A separate endpoint was created that returns this same shape with
 * minor differences (addition of `userActions` and extra fields on `auditItems`).
 * It turned out to be prohibitively difficult to modify the existing code to use
 * two types of issued item shapes, so I am just modifying this type and marking the
 * new fields as optional so that the generated type for the new endpoint can be
 * casted to the existing type and benefit from all the existing code that uses the
 * existing type.
 */
export interface IssuedItemLegacyDto {
    createdDate: string;
    dateIssued: string;
    voidDate: string;
    postDate?: string;
    clearedDate?: string;
    checkAmount: number;
    checkNumber: string;
    itemStatus: IssuedItemStatus;
    payee: string;
    type: IssuedItemType;
    account: AccountDto;
    isUploaded: boolean;
    auditItems: AuditItemDto[];
    entryType: IssuedItemEntryType;
    filename?: string;
    userActions?: UserActionDto[];
    accountReconItemDetailUniqueId?: string;
    index?: number;
}

/**
 *  @deprecated Use `IssuedItemSavedDto`.
 * The shape of the response when attempting to save a list of `IssuedItemDto` objects.
 */
export interface IssuedItemSavedLegacyDto extends IssuedItemLegacyDto {
    status: 'Submitted' | 'Failed';
    index: number;
    voidDate: string;
    errorSummary: {
        type: number;
        summaryMessage: string;
        summaryMessageList: string[];
        details: string;
        securityMessage: string;
        id: number;
        updatedBy: string;
        updatedDate: string;
    };
}

interface IssuedItemsQueryDtoBase {
    /**
     * Does this really need to be the full object or can it just be IDs?
     */
    accounts: AccountDto[];
    amountStart: string;
    amountEnd: string;
    itemType: Lowercase<IssuedItemLegacyDto['type']> | 'both';
    entryTypes: IssuedItemEntryType[];
    issuedDateStart: string;
    issuedDateEnd: string;
    createdDateStart: string;
    createdDateEnd: string;
    voidDateStart: string;
    voidDateEnd: string;
    clearedDateStart: string;
    clearedDateEnd: string;
    statusNames: IssuedItemLegacyDto['itemStatus'][];
    payee: string;
    checkNumber: string;
}

/**
 * Search parameters the API expects when querying for issued items.
 */
export type IssuedItemsQueryDto = Nullable<IssuedItemsQueryDtoBase>;

export interface UploadIssuedItemsError {
    lineNumber: number;
    errorText: string;
}

export interface IssuedItemsParsedResponse {
    fileErrors: UploadIssuedItemsError[];
    issuedItems: Nullable<IssuedItemSavedLegacyDto>[];
    issuedItemFileUniqueId?: string;
}

export function hasNullAccount(dto: Nullable<IssuedItemLegacyDto>) {
    const { account } = dto;
    if (!account) {
        return true;
    }

    return isNullAccount(account);
}
